<template>
  <div class="verify-page">

    <div class="verify-card">

      <!-- Logo / branding -->
      <div class="verify-brand">
        <img src="/img/logo.png" alt="Logo" class="verify-logo" @error="$event.target.style.display='none'" />
        <span class="verify-brand-name">Certificate Verification</span>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="verify-body verify-body--center">
        <div class="spinner"></div>
        <p class="verify-msg">Verifying certificate…</p>
      </div>

      <!-- Not found / error -->
      <div v-else-if="error" class="verify-body verify-body--center">
        <div class="status-icon status-icon--fail">
          <font-awesome-icon icon="times-circle" />
        </div>
        <h2 class="verify-title verify-title--fail">Certificate Not Found</h2>
        <p class="verify-desc">This certificate could not be verified. It may be invalid, revoked, or the link may be incorrect.</p>
      </div>

      <!-- Result -->
      <div v-else-if="cert" class="verify-body">

        <!-- Status icon -->
        <div :class="['status-icon', statusIconClass]">
          <font-awesome-icon :icon="statusIcon" />
        </div>

        <h2 :class="['verify-title', statusTitleClass]">{{ statusHeading }}</h2>
        <p class="verify-desc">{{ statusDesc }}</p>

        <!-- Certificate details -->
        <div class="cert-detail-card">
          <div class="cert-detail-row">
            <span class="cert-detail-label">Certificate No.</span>
            <span class="cert-detail-val cert-no-mono">{{ cert.certificate_no }}</span>
          </div>
          <div class="cert-detail-row">
            <span class="cert-detail-label">Issued To</span>
            <span class="cert-detail-val">{{ cert.employee_name }}</span>
          </div>
          <div class="cert-detail-row">
            <span class="cert-detail-label">Course</span>
            <span class="cert-detail-val">{{ cert.course_title }}</span>
          </div>
          <div class="cert-detail-row">
            <span class="cert-detail-label">Issue Date</span>
            <span class="cert-detail-val">{{ formatDate(cert.issued_at) }}</span>
          </div>
          <div class="cert-detail-row">
            <span class="cert-detail-label">Expiry Date</span>
            <span class="cert-detail-val">{{ cert.expires_at ? formatDate(cert.expires_at) : 'No expiry' }}</span>
          </div>
          <div class="cert-detail-row">
            <span class="cert-detail-label">Status</span>
            <span :class="['cert-status-badge', 'cert-status--' + cert.status]">{{ statusLabel }}</span>
          </div>
        </div>

        <p class="verify-footer-note">
          <font-awesome-icon icon="shield-alt" />
          This certificate was verified on {{ verifiedAt }}.
        </p>

      </div>

    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CertificateVerify',
  data() {
    return {
      loading: true,
      error: false,
      cert: null,
      verifiedAt: '',
    };
  },
  computed: {
    statusLabel() {
      return { valid: 'Valid', expired: 'Expired', revoked: 'Revoked' }[this.cert?.status] ?? this.cert?.status;
    },
    statusIcon() {
      return { valid: 'check-circle', expired: 'clock', revoked: 'ban' }[this.cert?.status] ?? 'check-circle';
    },
    statusIconClass() {
      return { valid: 'status-icon--pass', expired: 'status-icon--warn', revoked: 'status-icon--fail' }[this.cert?.status] ?? 'status-icon--pass';
    },
    statusTitleClass() {
      return { valid: 'verify-title--pass', expired: 'verify-title--warn', revoked: 'verify-title--fail' }[this.cert?.status] ?? '';
    },
    statusHeading() {
      return { valid: 'Certificate Verified', expired: 'Certificate Expired', revoked: 'Certificate Revoked' }[this.cert?.status] ?? 'Certificate Verified';
    },
    statusDesc() {
      return {
        valid:   'This is a genuine certificate issued by the organisation.',
        expired: 'This certificate was genuine but has passed its expiry date.',
        revoked: 'This certificate has been revoked and is no longer valid.',
      }[this.cert?.status] ?? '';
    },
  },
  async created() {
    const companyCode = this.$route.params.companyCode;
    const token       = this.$route.params.token;
    this.verifiedAt   = new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    // Use a clean axios instance — the global instance has a request interceptor that
    // injects the logged-in user's company code, which would double-prefix this URL.
    const api = axios.create({ baseURL: axios.defaults.baseURL });
    try {
      const { data } = await api.get(`/${companyCode}/certificates/verify/${token}`);
      this.cert = data.data;
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.verify-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f0f4fb 0%, #e8eef8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
}

.verify-card {
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(0, 74, 173, .10);
  width: 100%;
  max-width: 480px;
  overflow: hidden;
}

.verify-brand {
  background: #004AAD;
  padding: 20px 28px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.verify-logo { height: 32px; object-fit: contain; filter: brightness(0) invert(1); }
.verify-brand-name { color: #fff; font-size: 15px; font-weight: 700; letter-spacing: .02em; }

.verify-body { padding: 32px 28px; }
.verify-body--center { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 12px; }

/* Status icon */
.status-icon {
  width: 72px; height: 72px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 32px; margin: 0 auto 16px;
}
.status-icon--pass { background: #e8f5e9; color: #2d8a3e; }
.status-icon--warn { background: #fff8e6; color: #c07000; }
.status-icon--fail { background: #fff0f0; color: #c03030; }

.verify-title { font-size: 20px; font-weight: 800; color: #1a2b45; margin: 0 0 6px; text-align: center; }
.verify-title--pass { color: #2d8a3e; }
.verify-title--warn { color: #c07000; }
.verify-title--fail { color: #c03030; }

.verify-desc { font-size: 13px; color: #7a90b0; text-align: center; margin: 0 0 24px; line-height: 1.6; }
.verify-msg  { font-size: 14px; color: #7a90b0; margin: 0; }

/* Detail card */
.cert-detail-card {
  background: #f8fafd;
  border: 1px solid #e4eaf2;
  border-radius: 12px;
  overflow: hidden;
}
.cert-detail-row {
  display: flex;
  align-items: center;
  padding: 11px 16px;
  border-bottom: 1px solid #edf1f7;
  gap: 12px;
}
.cert-detail-row:last-child { border-bottom: none; }
.cert-detail-label { font-size: 12px; font-weight: 700; color: #a0b0c8; width: 120px; flex-shrink: 0; }
.cert-detail-val { font-size: 13px; font-weight: 500; color: #1a2b45; }
.cert-no-mono { font-family: monospace; font-size: 13px; }

.cert-status-badge { padding: 3px 10px; border-radius: 10px; font-size: 11px; font-weight: 700; }
.cert-status--valid   { background: #e8f5e9; color: #2e7d32; }
.cert-status--expired { background: #f5f5f5; color: #7a90b0; }
.cert-status--revoked { background: #fff0f0; color: #c03030; }

.verify-footer-note {
  margin: 20px 0 0;
  font-size: 11px;
  color: #a0b0c8;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

/* Spinner */
.spinner { width: 36px; height: 36px; border: 3px solid #e4eaf2; border-top-color: #004AAD; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
